import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { arquivosService, usersService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import '../styles/Arquivos.css';

const formatDate = (d) => {
  if (!d) return '-';
  const date = new Date(d);
  return date.toLocaleDateString('pt-BR') + ' ' + date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

const Arquivos = () => {
  const { isAdminOrDiretor } = useAuth();
  const [arquivos, setArquivos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [usuarios, setUsuarios] = useState([]);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadUserIds, setUploadUserIds] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  const loadArquivos = async () => {
    try {
      setLoading(true);
      const data = await arquivosService.list(busca);
      setArquivos(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erro ao carregar arquivos:', error);
      setArquivos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadArquivos();
  }, [busca]);

  useEffect(() => {
    if (isAdminOrDiretor()) {
      usersService.getAll().then((data) => setUsuarios(Array.isArray(data) ? data : [])).catch(() => setUsuarios([]));
    }
  }, []);

  const vendedoresESupervisores = usuarios.filter(u => u.role === 'vendedor' || u.role === 'supervisor');

  const [buscaUsuario, setBuscaUsuario] = useState('');
  const [filtroRole, setFiltroRole] = useState(''); // '' = todos, 'vendedor', 'supervisor'

  const usuariosFiltrados = vendedoresESupervisores.filter(u => {
    const matchRole = !filtroRole || u.role === filtroRole;
    const termo = (buscaUsuario || '').toLowerCase().trim();
    const matchBusca = !termo || (u.nome || '').toLowerCase().includes(termo) || (u.username || '').toLowerCase().includes(termo);
    return matchRole && matchBusca;
  });

  const selecionarTodosVisiveis = () => {
    const ids = usuariosFiltrados.map(u => u.id);
    setUploadUserIds(prev => {
      const set = new Set([...prev, ...ids]);
      return Array.from(set);
    });
  };

  const desmarcarTodos = () => {
    setUploadUserIds([]);
  };

  const desmarcarTodosVisiveis = () => {
    const idsVisiveis = new Set(usuariosFiltrados.map(u => u.id));
    setUploadUserIds(prev => prev.filter(id => !idsVisiveis.has(id)));
  };

  const removerSelecionado = (id) => {
    setUploadUserIds(prev => prev.filter(x => x !== id));
  };

  const usuariosSelecionados = vendedoresESupervisores.filter(u => uploadUserIds.includes(u.id));

  const handleUpload = async (e) => {
    e.preventDefault();
    setUploadError('');
    if (!uploadFile) {
      setUploadError('Selecione um arquivo.');
      return;
    }
    if (uploadUserIds.length === 0) {
      setUploadError('Selecione ao menos um vendedor ou supervisor.');
      return;
    }
    try {
      setUploading(true);
      await arquivosService.upload(uploadFile, uploadUserIds);
      setUploadFile(null);
      setUploadUserIds([]);
      await loadArquivos();
    } catch (error) {
      setUploadError(error.response?.data?.error || 'Erro ao enviar arquivo.');
    } finally {
      setUploading(false);
    }
  };

  const toggleUser = (id) => {
    setUploadUserIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleDownload = (id, nomeOriginal) => {
    arquivosService.download(id, nomeOriginal).catch(err => alert(err.response?.data?.error || 'Erro ao baixar'));
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir este arquivo? Esta ação não pode ser desfeita.')) return;
    try {
      setDeletingId(id);
      await arquivosService.delete(id);
      await loadArquivos();
    } catch (error) {
      alert(error.response?.data?.error || 'Erro ao excluir');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Layout>
      <div className="arquivos-page">
        <div className="page-header">
          <h1>Arquivos</h1>
          <p>Envie arquivos e defina quem pode baixá-los (admin/diretor). Vendedores e supervisores veem apenas os arquivos compartilhados com eles.</p>
        </div>

        {isAdminOrDiretor() && (
          <section className="arquivos-upload-section">
            <h2>Enviar arquivo</h2>
            <form onSubmit={handleUpload} className="arquivos-upload-form">
              {uploadError && <p className="arquivos-error">{uploadError}</p>}
              <div className="form-group">
                <label>Arquivo</label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.jpg,.jpeg,.png"
                  onChange={(e) => setUploadFile(e.target.files[0] || null)}
                  className="form-control"
                />
              </div>
              <div className="form-group arquivos-select-users">
                <label>Disponível para (vendedores e supervisores)</label>
                {usuariosSelecionados.length > 0 && (
                  <div className="arquivos-selecionados-resumo">
                    <span className="arquivos-selecionados-count">{usuariosSelecionados.length} selecionado{usuariosSelecionados.length !== 1 ? 's' : ''}</span>
                    <div className="arquivos-selecionados-chips">
                      {usuariosSelecionados.map(u => (
                        <span key={u.id} className="arquivos-chip">
                          {u.nome}
                          <button type="button" className="arquivos-chip-remove" onClick={() => removerSelecionado(u.id)} title="Remover" aria-label="Remover">×</button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div className="arquivos-user-filters">
                  <input
                    type="text"
                    placeholder="Buscar por nome ou usuário..."
                    value={buscaUsuario}
                    onChange={(e) => setBuscaUsuario(e.target.value)}
                    className="form-control arquivos-user-search"
                  />
                  <select
                    value={filtroRole}
                    onChange={(e) => setFiltroRole(e.target.value)}
                    className="form-control arquivos-role-filter"
                  >
                    <option value="">Todos</option>
                    <option value="vendedor">Só vendedores</option>
                    <option value="supervisor">Só supervisores</option>
                  </select>
                  <div className="arquivos-user-actions">
                    <button type="button" className="btn-link" onClick={selecionarTodosVisiveis}>
                      Selecionar todos (visíveis)
                    </button>
                    <span className="arquivos-actions-sep">|</span>
                    <button type="button" className="btn-link" onClick={desmarcarTodosVisiveis}>
                      Desmarcar visíveis
                    </button>
                    <span className="arquivos-actions-sep">|</span>
                    <button type="button" className="btn-link" onClick={desmarcarTodos}>
                      Desmarcar todos
                    </button>
                  </div>
                </div>
                <div className="arquivos-user-list-wrap">
                  <div className="arquivos-user-list">
                    {vendedoresESupervisores.length === 0 ? (
                      <span className="text-muted">Nenhum vendedor ou supervisor cadastrado.</span>
                    ) : usuariosFiltrados.length === 0 ? (
                      <span className="text-muted">Nenhum usuário encontrado com esse filtro.</span>
                    ) : (
                      usuariosFiltrados.map(u => (
                        <label key={u.id} className="arquivos-user-checkbox">
                          <input
                            type="checkbox"
                            checked={uploadUserIds.includes(u.id)}
                            onChange={() => toggleUser(u.id)}
                          />
                          <span className="arquivos-user-nome">{u.nome}</span>
                          <span className="arquivos-user-role">{u.role === 'supervisor' ? 'Sup.' : 'Vend.'}</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              </div>
              <button type="submit" className="btn-primary" disabled={uploading}>
                {uploading ? 'Enviando...' : 'Enviar'}
              </button>
            </form>
          </section>
        )}

        <section className="arquivos-list-section">
          <h2>Arquivos</h2>
          <div className="arquivos-filters">
            <input
              type="text"
              placeholder="Buscar por nome..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              className="form-control arquivos-search"
            />
          </div>
          {loading ? (
            <div className="loading">Carregando...</div>
          ) : arquivos.length === 0 ? (
            <p className="arquivos-empty">Nenhum arquivo encontrado.</p>
          ) : (
            <div className="arquivos-table-wrap">
              <table className="arquivos-table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Enviado em</th>
                    {isAdminOrDiretor() && <th>Disponível para</th>}
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {arquivos.map((arq) => (
                    <tr key={arq.id}>
                      <td>{arq.nome_original}</td>
                      <td>{formatDate(arq.created_at)} {arq.uploader && ` — ${arq.uploader.nome}`}</td>
                      {isAdminOrDiretor() && (
                        <td>
                          {arq.usuariosPermitidos && arq.usuariosPermitidos.length > 0
                            ? arq.usuariosPermitidos.map(u => u.nome).join(', ')
                            : '-'}
                        </td>
                      )}
                      <td>
                        <button
                          type="button"
                          className="btn-primary btn-sm"
                          onClick={() => handleDownload(arq.id, arq.nome_original)}
                        >
                          Baixar
                        </button>
                        {isAdminOrDiretor() && (
                          <button
                            type="button"
                            className="btn-secondary btn-sm btn-danger"
                            onClick={() => handleDelete(arq.id)}
                            disabled={deletingId === arq.id}
                          >
                            {deletingId === arq.id ? 'Excluindo...' : 'Excluir'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
};

export default Arquivos;
